// 発注検索条件を「URL を単一の情報源」として管理するフック。
//
// 復元の優先順位: URL パラメータ > localStorage > ロール既定値
//  ・URL に検索パラメータが1つでもあればそれを使う(欠けはロール既定値で補完)
//  ・無ければ localStorage の保存値(正規形クエリ文字列)を復元し、URL へ replace で反映
//  ・どちらも無ければロール既定値を URL へ明示する
// 以後の変更はすべて URL の replace として行い(戻る履歴を汚さない)、
// 変更のたび localStorage にも保存する(発注番号 num だけは保存しない)。
//
// state を別に持たず URL から都度導出することで、URL・画面・保存値の
// 三者がズレる余地をなくしている。権限クランプは decode(→normalize)内で常時かかるため、
// URL や保存値に権限外の値が入っていてもこのフックからは出てこない。
//
// 実装上の要点:
//  ・書き込み系(applyConditions / patchConditions)は useLocation のスナップショットではなく
//    常に history.location(現在値)を読む。setTimeout やイベントハンドラが古い関数を
//    掴んでいても「最新の URL に差分を当てる」ことが保証され、
//    直前の別フィルタ変更を巻き戻す事故(stale closure)が起きない。
//  ・復元はマウント時1回ではなく「URL からパラメータが消えるたび」に行う。
//    ドロワーで現在の画面をもう一度選ぶと push で search が空になるが、
//    react-router は同一ルートを再マウントしないため、マウント1回の初期化では戻せない。
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserInfo } from "../reducks/users/selectors";
import {
  decodeSearchParams,
  encodeSearchParams,
} from "../functions/order/orderSearchCodec";
import {
  getDefaultConditions,
  normalizeConditions,
} from "../functions/order/orderSearchModel";
import {
  loadStoredSearchParams,
  saveStoredSearchParams,
} from "../functions/order/orderSearchStorage";

export const useOrderSearchConditions = (screen) => {
  const history = useHistory();
  const location = useLocation();
  const userInfo = useSelector(getUserInfo);
  // userInfo はオブジェクト全体だと無関係な更新(カート等)でも識別子が変わるため、
  // 条件の導出に影響するフィールドだけを取り出して依存にする
  const { uid, role, order_department_id: ownDepartmentId } = userInfo;

  const { conditions, hasParams } = useMemo(
    () =>
      decodeSearchParams(location.search, {
        screen,
        userInfo: { role, order_department_id: ownDepartmentId },
      }),
    [location.search, screen, role, ownDepartmentId]
  );

  // 次の条件を正規形 URL に反映する(同一なら何もしない = 無駄な replace の防止)。
  // history は react-router v5 では同一インスタンスが返るため、この関数の識別子は安定し、
  // かつ history.location 経由で常に現在の URL と比較できる。
  const applyConditions = useCallback(
    (nextConditions) => {
      const { search } = history.location;
      const nextSearch = encodeSearchParams(nextConditions);
      const currentSearch = search.startsWith("?") ? search.slice(1) : search;
      if (nextSearch !== currentSearch) {
        // location を丸ごと引き継ぐ(hash や location.state を落とさない)
        history.replace({ ...history.location, search: `?${nextSearch}` });
      }
    },
    [history]
  );

  // 部分更新。差分は「現在の URL から読み直した条件」に対して当てる
  // (レンダー時点の conditions に当てると、デバウンス等で古い関数が呼ばれた場合に
  //  その後の変更を巻き戻してしまう)
  const patchConditions = useCallback(
    (patch) => {
      const slimUserInfo = { role, order_department_id: ownDepartmentId };
      const { conditions: current } = decodeSearchParams(
        history.location.search,
        { screen, userInfo: slimUserInfo }
      );
      applyConditions(
        normalizeConditions(slimUserInfo, { ...current, ...patch })
      );
    },
    [applyConditions, history, screen, role, ownDepartmentId]
  );

  const resetConditions = useCallback(() => {
    applyConditions(
      getDefaultConditions(screen, {
        role,
        order_department_id: ownDepartmentId,
      })
    );
  }, [applyConditions, screen, role, ownDepartmentId]);

  // 復元と正規形化。
  //  ・URL に検索パラメータが無い(初回マウント、またはドロワー等で search 無しの
  //    同一画面へ push された)場合: localStorage → ロール既定値 の順で URL へ反映する
  //  ・URL にある場合: 正規形と違えば整える(applyConditions は同一なら何もしないため、
  //    正規形が不動点であることと合わせて replace がループすることはない。
  //    往復の不動点性は orderSearchCodec.test.js で固定している)
  useEffect(() => {
    if (hasParams) {
      applyConditions(conditions);
      return;
    }
    const stored = loadStoredSearchParams(screen, uid);
    if (stored) {
      const { conditions: restored } = decodeSearchParams(stored, {
        screen,
        userInfo: { role, order_department_id: ownDepartmentId },
      });
      applyConditions(restored);
    } else {
      // 既定値も URL に明示する(この時点から URL が唯一の情報源になる)
      applyConditions(conditions);
    }
    // conditions/applyConditions は location.search から導出されるため、
    // 発火条件として意味を持つのは search(と hasParams)の変化だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasParams, location.search, screen, uid]);

  // 変更を localStorage へ保存する。
  // URL 直打ちや共有リンクで開いた場合も「最後に使った条件」として保存する。
  // 発注番号(num)は保存しない(古い番号が残ると次回一覧が空になり原因が分かりにくい)。
  useEffect(() => {
    if (!hasParams) return; // 復元前(URL 反映前)の既定値を保存しない
    saveStoredSearchParams(
      screen,
      uid,
      encodeSearchParams(conditions, { includeOrderNumber: false })
    );
  }, [conditions, hasParams, screen, uid]);

  return {
    conditions,
    patchConditions,
    resetConditions,
    // 復元(LS 反映)前に既定値で購読を始めてしまわないためのゲート。
    // 復元後は URL に必ずパラメータが入るので hasParams がそのまま準備完了を表す
    isReady: hasParams,
  };
};

export default useOrderSearchConditions;
